/**
 * Cart quantities, counted honestly. Ported from Flossr-e-commerce
 * (src/lib/utils/cart-quantity.js, commit 6091068) - same Shopify behaviour,
 * same failure, so the same helpers.
 *
 * Shopify does NOT reject an add-to-cart for a country with no market or no
 * shipping coverage. `cartLinesAdd` succeeds and returns the line with
 * `quantity: 0` and `cost.totalAmount: 0.0`, priced in the primary market's
 * currency - no userError, no exception. Counting *lines* instead of *units*
 * then renders "CART (1)" over a cart holding zero units, and checkout dies
 * later with an unexplained "Cart is empty".
 *
 * In flossr that was Gorgias 76202682 (a customer in CA), reproduced against
 * production for CA, CH, AU, NZ, JP, SG, AE, ZA, MX, BR, IN, KR, TR, UA, IL
 * and SA. GLORB counted lines in exactly the same three places: the header
 * badge, the drawer's own count, and the checkout gate.
 *
 * The real fix for an uncovered country is market + shipping-profile coverage
 * in the Shopify admin. These helpers make the broken state impossible to
 * render as "you have items in your cart", so it fails loudly instead.
 *
 * Every helper tolerates both `{ node }` edges (what `cartStore.items` holds,
 * straight off `lines.edges`) and bare nodes (what `addToCart` returns).
 */

import { regionUnavailableMessage } from "#lib/utils/region-message.js";

/** Units on a single cart line. Anything unparseable or negative counts as 0. */
export function lineQuantity(item) {
  const node = item?.node || item || {};
  const quantity = Number(node.quantity);
  return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
}

/** Total units across the cart. This is what a cart badge should show. */
export function cartUnitCount(items) {
  if (!Array.isArray(items)) return 0;
  return items.reduce((total, item) => total + lineQuantity(item), 0);
}

/**
 * A cart is empty when it holds no *units* - having lines is not enough.
 * Use this for every "is the cart empty" gate, including the checkout handoff.
 */
export function isCartEmpty(items) {
  return cartUnitCount(items) === 0;
}

/**
 * Lines Shopify accepted but zeroed out - the signature of merchandise that
 * isn't purchasable in the cart's country.
 */
export function zeroedLines(items) {
  if (!Array.isArray(items)) return [];
  return items.filter((item) => lineQuantity(item) === 0);
}

/**
 * True when `variantId` came back from Shopify with zero units, i.e. the add
 * silently did nothing. Callers must treat this as a failed add.
 */
export function wasLineZeroed(items, variantId) {
  if (!Array.isArray(items) || !variantId) return false;
  return items.some((item) => {
    const node = item?.node || item || {};
    return node?.merchandise?.id === variantId && lineQuantity(item) === 0;
  });
}

/**
 * Why a checkout handoff cannot proceed, or null when it can.
 *
 * Three different situations used to report the same thing. The drawer's
 * handleCheckout refetches the cart first, and when that read failed the
 * shopper - and the `checkout_handoff_failed` event - were both told "Cart is
 * empty". A cart that cannot be read is not an empty cart: the shopper still
 * has items and can retry, and the two need telling apart in the data.
 *
 * Returns `{ reason, message }` - `reason` for the event, `message` for the
 * shopper.
 *
 * @param {{ items?: any[], error?: unknown }} [state]
 */
export function checkoutBlockReason({ items, error, country } = {}) {
  // A zeroed line is a definite answer (the country has no coverage), so it
  // wins even if the last fetch also errored.
  if (zeroedLines(items).length) {
    return {
      reason: "region_unavailable",
      // The same sentence the add-to-cart refusal gives, from the same place:
      // one situation the shopper meets down two paths, and two different
      // wordings teach them not to trust either.
      message: regionUnavailableMessage(country),
    };
  }

  if (!isCartEmpty(items)) return null;

  if (error) {
    return {
      reason: "cart_unreadable",
      message: "We couldn't reach your cart - please try again.",
    };
  }

  return { reason: "cart_empty", message: "Cart is empty" };
}
